import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  List,
  ListItem,
  Checkbox,
  Typography,
  TextField,
  Box,
  CircularProgress,
  Chip,
  Alert,
  Paper,
  Popper,
  ClickAwayListener,
  Stack,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { SearchInvoiceReceivableResponseDetailedModel } from "../models/SearchModels";
import { useSearchInvoiceReceivables } from "../hooks/useSearchInvoiceReceivables";
import { useDebouncedValue } from "../hooks/useDebounceValue";

interface ReceivablesSelectionPopperProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onConfirm: (selectedFiles: SearchInvoiceReceivableResponseDetailedModel[]) => void;
  initialSelected?: SearchInvoiceReceivableResponseDetailedModel[];
  highlightedContactIds?: string[];
}

const ReceivablesSelectionPopper: React.FC<ReceivablesSelectionPopperProps> = ({
  open,
  anchorEl,
  onClose,
  onConfirm,
  initialSelected = [],
  highlightedContactIds = [],
}) => {
  const [selectedItems, setSelectedItems] = useState<SearchInvoiceReceivableResponseDetailedModel[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useDebouncedValue("", 500);

  const { result: searchResults, isLoading } = useSearchInvoiceReceivables(debouncedSearchTerm);

  useEffect(() => {
    if (open) {
      setSelectedItems(initialSelected);
    }
    setSearchTerm("");
    setDebouncedSearchTerm("");
  }, [open, setDebouncedSearchTerm, initialSelected]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    setDebouncedSearchTerm(value);
  };

  const handleToggle = (item: SearchInvoiceReceivableResponseDetailedModel) => () => {
    const currentIndex = selectedItems.findIndex((i) => i.id === item.id);
    const newSelected = [...selectedItems];

    if (currentIndex === -1) {
      newSelected.push(item);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    setSelectedItems(newSelected);
  };

  const handleConfirm = () => {
    onConfirm(selectedItems);
    onClose();
  };

  const isSelected = (id: string) => selectedItems.some((i) => i.id === id);
  const isHighlighted = (contactId: string) => highlightedContactIds.includes(contactId);

  // Sort results to show current contact's receivables first
  const sortedSearchResults = useMemo(() => {
    if (!searchResults) return [];
    return [...searchResults].sort((a, b) => {
      const aHighlighted = highlightedContactIds.includes(a.contact.id);
      const bHighlighted = highlightedContactIds.includes(b.contact.id);
      if (aHighlighted && !bHighlighted) return -1;
      if (!aHighlighted && bHighlighted) return 1;
      return 0;
    });
  }, [searchResults, highlightedContactIds]);

  // Count selected items that are not from highlighted contacts
  const unrelatedSelectionCount = useMemo(() => {
    if (highlightedContactIds.length === 0 || selectedItems.length === 0) return 0;
    return selectedItems.filter((item) => !highlightedContactIds.includes(item.contact.id)).length;
  }, [selectedItems, highlightedContactIds]);
  const hasNotFoundContact = Boolean(highlightedContactIds.length === 0);

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      style={{ zIndex: 1300, width: anchorEl?.clientWidth }}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          elevation={8}
          sx={{
            mt: 1,
            maxHeight: 400,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2), 0 4px 12px rgba(0, 0, 0, 0.15)",
          }}
        >
          {/* Header */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Select Receivables</Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              {selectedItems.length > 0 && (
                <Button
                  size="small"
                  onClick={() => setSelectedItems([])}
                  sx={{ textTransform: "none", fontSize: 12, minWidth: "auto", py: 0 }}
                >
                  Clear ({selectedItems.length})
                </Button>
              )}
              <IconButton size="small" onClick={onClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>

          {/* Search */}
          <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search Receivables..."
              value={searchTerm}
              onChange={handleSearchChange}
              size="small"
              autoFocus
            />
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflow: "auto", minHeight: 0, maxHeight: 200 }}>
            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : sortedSearchResults.length === 0 ? (
              <Typography color="textSecondary" align="center" sx={{ py: 2, fontSize: 13 }}>
                {searchTerm ? "No receivables found." : "Start typing to search."}
              </Typography>
            ) : (
              <List sx={{ pt: 0, px: 1 }}>
                {sortedSearchResults.map((att) => {
                  const labelId = `checkbox-list-label-${att.id}`;
                  const isChecked = isSelected(att.id);
                  const isContactHighlighted = isHighlighted(att.contact.id);
                  return (
                    <ListItem
                      key={att.id}
                      disablePadding
                      sx={{
                        mb: 0.5,
                        border: "1px solid",
                        borderColor: isContactHighlighted ? "primary.main" : "divider",
                        borderRadius: 1,
                        bgcolor: isContactHighlighted ? "primary.50" : "transparent",
                      }}
                    >
                      <Box
                        onClick={handleToggle(att)}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          width: "100%",
                          py: 0.75,
                          px: 1.5,
                          cursor: "pointer",
                          "&:hover": {
                            bgcolor: isContactHighlighted ? "primary.100" : "action.hover",
                          },
                        }}
                      >
                        <Checkbox
                          edge="start"
                          checked={isChecked}
                          tabIndex={-1}
                          disableRipple
                          inputProps={{ "aria-labelledby": labelId }}
                          sx={{
                            mr: 1,
                            p: 0.25,
                            mt: 0.25,
                            "& .MuiSvgIcon-root": { fontSize: 18 },
                            color: "text.secondary",
                            "&.Mui-checked": {
                              color: "text.primary",
                            },
                          }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box
                            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.25 }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <Typography id={labelId} variant="body2" sx={{ fontWeight: 600, fontSize: 12 }}>
                                {att.invoiceNumber}
                              </Typography>
                              {isContactHighlighted && (
                                <Chip
                                  label="Current"
                                  size="small"
                                  color="primary"
                                  sx={{ height: 16, fontSize: 9, fontWeight: 500 }}
                                />
                              )}
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 12, color: "primary.main" }}>
                              {att.currency}{" "}
                              {att.amount.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: 11, color: "text.secondary", wordBreak: "break-word" }}
                          >
                            {att.contact.companyName}
                          </Typography>
                        </Box>
                      </Box>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>

          {/* Warnings */}
          {unrelatedSelectionCount > 0 && (
            <Alert severity="warning" sx={{ mx: 1, mb: 0.5, py: 0, fontSize: 11 }}>
              {unrelatedSelectionCount} receivable{unrelatedSelectionCount !== 1 ? "s" : ""} not related to current
              contact.
            </Alert>
          )}
          {hasNotFoundContact && (
            <Alert severity="warning" sx={{ mx: 1, mb: 0.5, py: 0, fontSize: 11 }}>
              No contact found with this email address.
            </Alert>
          )}

          {/* Actions */}
          <Stack direction="row" spacing={1} sx={{ p: 1.5, borderTop: 1, borderColor: "divider" }}>
            <Button onClick={onClose} variant="outlined" color="inherit" size="small" fullWidth>
              Cancel
            </Button>
            <Button onClick={handleConfirm} variant="contained" color="primary" size="small" fullWidth>
              {selectedItems.length === 0 ? "Select none" : `Select (${selectedItems.length})`}
            </Button>
          </Stack>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

export default ReceivablesSelectionPopper;
